import { showErrorToast } from "../../configurator/renderer/tools/helper";
import { useUiTranslator } from "../../hooks/useUiTranslator";
import { usePostApiNeedHelpNeedHelp } from "../../openapi/backendApi";
import { emailRegex } from "../../tools/regex";
import { BaseModal } from "../common/BaseModal/BaseModal";
import { LabeledInput } from "../common/LabeledInput/LabeledInput";

import { ReactNode, useState } from "react";

import { useLanguage } from "@4pace-cpq/core";
import { useGoogleReCaptcha } from "@google-recaptcha/react";
import { Box, Button } from "@mui/material";
import Grid from "@mui/material/Grid";

export const BtnNeedHelp = (): ReactNode => {
  const t = useUiTranslator();
  const { selectedLanguage } = useLanguage();

  const googleReCaptcha = useGoogleReCaptcha();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [formInquiry, setFormInquiry] = useState<string>("");
  const [formUserName, setFormUserName] = useState<string>("");
  const [formUserEmail, setFormUserEmail] = useState<string>("");
  const [formPhoneNumber, setFormPhoneNumber] = useState<string>("");
  const [formUserCompany, setFormUserCompany] = useState<string>("");
  const [formUserCountry, setFormUserCountry] = useState<string>("");
  const [formUserRegion, setFormUserRegion] = useState<string>("");
  const { mutate: mutateHelpRequest } = usePostApiNeedHelpNeedHelp();
  const [status, setStatus] = useState<string>("init");

  const handleCloseModal = (): void => {
    setIsModalOpen(false);
  };
  const handleOpenModal = (): void => {
    setIsModalOpen(true);
  };
  const handleSubmit = async (): Promise<void> => {
    setIsLoading(true);
    setStatus("default");
    if (googleReCaptcha && typeof googleReCaptcha.executeV3 === "function") {
      let recaptchaToken: string;
      try {
        recaptchaToken = await googleReCaptcha.executeV3("action");
      } catch (error) {
        console.warn("Recaptcha execution failed", error);
        setStatus("error");
        setIsLoading(false);
        showErrorToast(
          t(
            "CustomApp:toast.error.generic",
            "An error occurred while processing your request.",
          ),
        );
        return;
      }
      mutateHelpRequest(
        {
          data: {
            name: formUserName,
            eMail: formUserEmail,
            phoneNumber: formPhoneNumber,
            company: formUserCompany,
            country: formUserCountry,
            region: formUserRegion,
            comment: formInquiry,
            recaptchaToken: recaptchaToken,
            language: selectedLanguage?.languageCode ?? "en-US",
          },
        },
        {
          onSuccess: () => {
            setStatus("success");
            setIsLoading(false);
            handleCloseModal();
          },
          onError: (error) => {
            console.error(error);
            setStatus("error");
            console.warn("Error sending help request", error);
            setIsLoading(false);
            const body = error.info as
              { key?: string; params?: Record<string, string> } | undefined;
            showErrorToast(
              t(
                body?.key
                  ? `CustomApp:${body.key}`
                  : "CustomApp:toast.error.generic",
                "An error occurred while processing your request.",
                { params: body?.params },
              ),
            );
          },
        },
      );
    } else {
      console.error("Recaptcha Error");
    }
  };

  const formNotValid =
    !formInquiry || !formUserName || !emailRegex.test(formUserEmail);

  return (
    <>
      <Button
        variant="text"
        onClick={handleOpenModal}
        sx={{ textTransform: "uppercase" }}
      >
        {t("CustomApp:preselector.page1.linkText", "Contact us")}
      </Button>
      <BaseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={t("CustomApp:preselector.page1.linkText", "Contact us")}
        customSx={{
          minWidth: "30em",
          width: "25%",
        }}
        isLoading={isLoading}
      >
        <Box>
          <form>
            <Grid container columnSpacing={1} rowSpacing={2}>
              <Grid size={12}>
                <LabeledInput
                  required
                  fullWidth={true}
                  label={t(
                    "CustomApp:label.form.userInquiry",
                    "What is your inquiry?",
                  )}
                  multiline
                  rows={4}
                  value={formInquiry}
                  handleChange={(val) => setFormInquiry(val)}
                />
              </Grid>
              <Grid size={6}>
                <LabeledInput
                  required
                  fullWidth={true}
                  label={t("CustomApp:label.form.name", "Name")}
                  value={formUserName}
                  handleChange={(val) => setFormUserName(val)}
                />
              </Grid>
              <Grid size={6}>
                <LabeledInput
                  required
                  fullWidth={true}
                  label={t("CustomApp:label.form.email", "E-Mail")}
                  value={formUserEmail}
                  handleChange={(val) => setFormUserEmail(val)}
                />
              </Grid>
              <Grid size={6}>
                <LabeledInput
                  fullWidth={true}
                  label={t("CustomApp:label.form.phoneNumber", "Phone number")}
                  value={formPhoneNumber}
                  handleChange={(val) => setFormPhoneNumber(val)}
                />
              </Grid>
              <Grid size={6}>
                <LabeledInput
                  fullWidth={true}
                  label={t("CustomApp:label.form.company", "Company")}
                  value={formUserCompany}
                  handleChange={(val) => setFormUserCompany(val)}
                />
              </Grid>
              <Grid size={6}>
                <LabeledInput
                  fullWidth={true}
                  label={t("CustomApp:label.form.country", "Country")}
                  value={formUserCountry}
                  handleChange={(val) => setFormUserCountry(val)}
                />
              </Grid>
              <Grid size={6}>
                <LabeledInput
                  fullWidth={true}
                  label={t("CustomApp:label.form.formRegion", "Region")}
                  value={formUserRegion}
                  handleChange={(val) => setFormUserRegion(val)}
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                variant="contained"
                color={
                  status === "success"
                    ? "success"
                    : status === "error"
                      ? "error"
                      : "primary"
                }
                disabled={formNotValid}
                onClick={handleSubmit}
              >
                {t("CustomApp:button.send", "Send")}
              </Button>
            </Box>
          </form>
        </Box>
      </BaseModal>
    </>
  );
};
